import { toPng, toBlob } from "html-to-image";
import { log } from "./logger";

/**
 * Export padding configuration (in pixels)
 * Adjust these values to control spacing around the exported dashboard
 */
const EXPORT_PADDING = {
  top: 85,
  bottom: 0,
  left: 0,
  right: 0,
} as const;

type ScrollContainerSnapshot = {
  element: HTMLElement;
  classList: {
    overflowXAuto: boolean;
    overflowYAuto: boolean;
  };
  style: {
    overflow: string;
    overflowX: string;
    overflowY: string;
    maxHeight: string;
    height: string;
    maxWidth: string;
    width: string;
    display: string;
  };
};

async function blobFromCanvas(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (!blob) {
          reject(new Error("Could not create final blob"));
          return;
        }
        resolve(blob);
      },
      "image/png",
      1.0,
    );
  });
}

export async function exportElementAsPng(
  element: HTMLElement,
  filename: string,
  options: Parameters<typeof toPng>[1],
): Promise<void> {
  try {
    const dataUrl = await toPng(element, options);
    const link = document.createElement("a");
    link.download = filename;
    link.href = dataUrl;
    link.click();
  } catch (error) {
    const detail = error instanceof Error ? error.message : String(error);
    throw new Error(`Failed to export ${filename}: ${detail}`);
  }
}

function adjustScrollContainersForExport(): () => void {
  const containers = Array.from(
    document.querySelectorAll<HTMLElement>(
      "[data-export-disable-scroll], .overflow-x-auto, .overflow-y-auto",
    ),
  );

  if (containers.length === 0) {
    return () => {};
  }

  const snapshots: ScrollContainerSnapshot[] = containers.map((element) => ({
    element,
    classList: {
      overflowXAuto: element.classList.contains("overflow-x-auto"),
      overflowYAuto: element.classList.contains("overflow-y-auto"),
    },
    style: {
      overflow: element.style.overflow,
      overflowX: element.style.overflowX,
      overflowY: element.style.overflowY,
      maxHeight: element.style.maxHeight,
      height: element.style.height,
      maxWidth: element.style.maxWidth,
      width: element.style.width,
      display: element.style.display,
    },
  }));

  containers.forEach((container) => {
    container.classList.remove("overflow-x-auto", "overflow-y-auto");
    container.style.setProperty("overflow", "visible", "important");
    container.style.setProperty("overflow-x", "visible", "important");
    container.style.setProperty("overflow-y", "visible", "important");
    container.style.setProperty("max-height", "none", "important");
    container.style.setProperty("height", "auto", "important");
    container.style.setProperty("max-width", "none", "important");
    container.style.setProperty("width", "auto", "important");
    container.style.setProperty("display", "block", "important");
  });

  return () => {
    snapshots.forEach((snapshot) => {
      const { element, classList, style } = snapshot;
      if (classList.overflowXAuto) {
        element.classList.add("overflow-x-auto");
      }
      if (classList.overflowYAuto) {
        element.classList.add("overflow-y-auto");
      }
      element.style.overflow = style.overflow;
      element.style.overflowX = style.overflowX;
      element.style.overflowY = style.overflowY;
      element.style.maxHeight = style.maxHeight;
      element.style.height = style.height;
      element.style.maxWidth = style.maxWidth;
      element.style.width = style.width;
      element.style.display = style.display;
    });
  };
}

/**
 * Alternative method that captures full viewport then crops
 */
export const exportDashboardFullViewport = async (): Promise<void> => {
  let restoreScrollContainers: () => void = () => {};
  try {
    log.info("Capturing dashboard content", {}, "export");

    // Find dashboard element
    const dashboardElement = document.querySelector(
      "[data-dashboard]",
    ) as HTMLElement;
    if (!dashboardElement) {
      throw new Error("Dashboard element not found");
    }

    // Scroll to top
    window.scrollTo(0, 0);
    await waitForPageLoad();

    // Force export background to white regardless of active theme
    const backgroundColor = "#ffffff";

    // Get actual content dimensions
    const dashboardRect = dashboardElement.getBoundingClientRect();
    const contentWidth = Math.max(
      dashboardElement.scrollWidth,
      dashboardElement.offsetWidth,
    );
    const contentHeight = Math.max(
      dashboardElement.scrollHeight,
      dashboardElement.offsetHeight,
    );

    log.info(
      "Dashboard dimensions",
      {
        width: contentWidth,
        height: contentHeight,
        padding: EXPORT_PADDING,
      },
      "export",
    );

    // Temporarily adjust scroll containers on original document
    restoreScrollContainers = adjustScrollContainersForExport();

    // Capture dashboard element directly
    const blob = await toBlob(dashboardElement, {
      quality: 1.0,
      pixelRatio: 2,
      backgroundColor: backgroundColor,
      width: contentWidth,
      height: contentHeight,
      style: {
        margin: "0",
        padding: "0",
      },
      // Filter to exclude elements marked for export exclusion
      filter: (node) => {
        const element = node as HTMLElement;
        if (element.tagName === "SCRIPT" || element.tagName === "STYLE") {
          return false;
        }
        // Exclude elements marked for export exclusion
        if (
          element.classList?.contains("data-exclude-from-export") ||
          element.hasAttribute?.("data-exclude-from-export")
        ) {
          return false;
        }
        return true;
      },
      onclone: (clonedDoc) => {
        const doc =
          clonedDoc instanceof Document ? clonedDoc : clonedDoc.ownerDocument;
        const root = doc ?? document;
        const containers = root.querySelectorAll<HTMLElement>(
          "[data-export-disable-scroll]",
        );
        containers.forEach((container) => {
          container.classList.remove("overflow-x-auto", "overflow-y-auto");
          container.style.setProperty("overflow-x", "visible", "important");
          container.style.setProperty("overflow-y", "visible", "important");
          container.style.overflow = "visible";
          container.style.maxHeight = "none";
          container.style.height = "auto";
          container.style.setProperty("max-width", "none", "important");
          container.style.setProperty("width", "auto", "important");
          container.style.setProperty("display", "block", "important");
        });
      },
    });

    if (!blob) {
      throw new Error("Failed to capture dashboard");
    }

    // Create image from blob to add padding
    const img = new Image();
    const imgUrl = URL.createObjectURL(blob);

    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = imgUrl;
    });

    // Create canvas with configurable padding on each side
    const paddedWidth =
      img.width + (EXPORT_PADDING.left + EXPORT_PADDING.right) * 2; // *2 for pixelRatio
    const paddedHeight =
      img.height + (EXPORT_PADDING.top + EXPORT_PADDING.bottom) * 2;

    const canvas = document.createElement("canvas");
    canvas.width = paddedWidth;
    canvas.height = paddedHeight;

    const ctx = canvas.getContext("2d");
    if (!ctx) {
      throw new Error("Could not get canvas context");
    }

    // Fill background
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, paddedWidth, paddedHeight);

    // Draw image with configurable padding on each side
    ctx.drawImage(img, EXPORT_PADDING.left * 2, EXPORT_PADDING.top * 2);

    // Clean up
    URL.revokeObjectURL(imgUrl);

    const finalBlob = await blobFromCanvas(canvas);
    downloadBlob(finalBlob, generateFilename());
    log.info(
      "Screenshot exported successfully",
      {
        width: paddedWidth,
        height: paddedHeight,
        padding: EXPORT_PADDING,
      },
      "export",
    );
  } catch (error) {
    log.error("Dashboard capture failed", error, "export");
    await exportDashboardSimplified();
  } finally {
    restoreScrollContainers();
  }
};

/**
 * Updated main export to try viewport method first
 */
export const exportDashboardScreenshot = async (): Promise<void> => {
  // Try full viewport method first as it handles positioning better
  return exportDashboardFullViewport();
};

/**
 * Main export function using html-to-image for better rendering
 */
export const exportDashboardWithHtmlToImage = async (): Promise<void> => {
  let restoreScrollContainers: () => void = () => {};
  try {
    log.info("Generating dashboard screenshot", {}, "export");

    // Find the dashboard element
    const dashboardElement =
      (document.querySelector("[data-dashboard]") as HTMLElement) ||
      document.body;

    // Get the actual position and dimensions of the dashboard
    const dashboardRect = dashboardElement.getBoundingClientRect();
    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
    const scrollLeft =
      window.pageXOffset || document.documentElement.scrollLeft;

    // Calculate absolute position
    const absoluteTop = dashboardRect.top + scrollTop;
    const absoluteLeft = dashboardRect.left + scrollLeft;

    // Scroll to top to ensure consistent capture
    window.scrollTo(0, 0);

    // Wait for everything to settle
    await waitForPageLoad();

    // Detect theme for other styling tweaks, but keep exports on a white background
    const isDarkMode = document.documentElement.classList.contains("dark");
    const backgroundColor = "#ffffff";

    // Get the actual content dimensions (excluding margins)
    const contentWidth = Math.max(
      dashboardElement.scrollWidth,
      dashboardElement.offsetWidth,
      dashboardElement.clientWidth,
      1920, // minimum width for dashboard
    );

    const contentHeight = Math.max(
      dashboardElement.scrollHeight,
      dashboardElement.offsetHeight,
      dashboardElement.clientHeight,
    );

    log.debug(
      "Capturing dashboard",
      {
        width: contentWidth,
        height: contentHeight,
        top: absoluteTop,
        left: absoluteLeft,
      },
      "export",
    );

    // Temporarily adjust scroll containers on original document
    restoreScrollContainers = adjustScrollContainersForExport();

    // First attempt: Capture as blob for better performance
    const blob = await toBlob(dashboardElement, {
      quality: 1.0,
      pixelRatio: 2, // 2x for high quality
      width: contentWidth,
      height: contentHeight,
      backgroundColor: backgroundColor,
      cacheBust: true,

      // Style modifications
      style: {
        // Ensure full width is captured
        width: `${contentWidth}px`,
        minWidth: `${contentWidth}px`,
        maxWidth: `${contentWidth}px`,
        height: `${contentHeight}px`,

        // Reset all positioning and spacing
        margin: "0",
        padding: "0",
        position: "relative",
        top: "0",
        left: "0",
        transform: "translate(0, 0)",
        boxSizing: "border-box",

        // Ensure text renders properly
        WebkitFontSmoothing: "antialiased",
        MozOsxFontSmoothing: "grayscale",
        textRendering: "optimizeLegibility",
      },

      // Skip loading fonts from CDNs to avoid CORS issues
      skipFonts: false,
      preferredFontFormat: "woff2",

      // Include all fonts
      fontEmbedCSS: `
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
      `,

      // Filter to exclude problematic elements
      filter: (node) => {
        // Exclude elements that might cause issues
        const element = node as HTMLElement;
        if (element.tagName === "SCRIPT" || element.tagName === "STYLE") {
          return false;
        }
        // Exclude elements marked for export exclusion
        if (
          element.classList?.contains("data-exclude-from-export") ||
          element.hasAttribute?.("data-exclude-from-export")
        ) {
          return false;
        }
        return true;
      },

      // Clone callback to fix rendering issues
      onclone: async (clonedDoc, targetNode) => {
        const clonedElement = targetNode as HTMLElement;

        // Reset any margins or padding on the root element
        clonedElement.style.margin = "0";
        clonedElement.style.padding = "0";
        clonedElement.style.position = "relative";
        clonedElement.style.top = "0";
        clonedElement.style.left = "0";
        clonedElement.style.transform = "none";

        // Also reset on the body to ensure no inherited margins
        if (clonedDoc.body) {
          clonedDoc.body.style.margin = "0";
          clonedDoc.body.style.padding = "0";
        }

        // Ensure tables render without scrollbars during export
        const exportScrollContainers =
          clonedDoc.querySelectorAll<HTMLElement>(
            "[data-export-disable-scroll]",
          );
        exportScrollContainers.forEach((container) => {
          container.classList.remove("overflow-x-auto", "overflow-y-auto");
          container.style.setProperty("overflow-x", "visible", "important");
          container.style.setProperty("overflow-y", "visible", "important");
          container.style.overflow = "visible";
          container.style.maxHeight = "none";
          container.style.height = "auto";
          container.style.setProperty("max-width", "none", "important");
          container.style.setProperty("width", "auto", "important");
          container.style.setProperty("display", "block", "important");
        });

        // Wait for fonts in the cloned document
        if (clonedDoc.fonts) {
          await clonedDoc.fonts.ready;
        }

        // Fix text spacing issues
        const allTextElements = clonedElement.querySelectorAll(
          "h1, h2, h3, h4, h5, h6, p, span, div",
        );
        allTextElements.forEach((el) => {
          const element = el as HTMLElement;
          const text = element.textContent || "";

          // Fix camelCase text spacing
          if (/[a-z][A-Z]/.test(text) && !text.includes(" ")) {
            element.textContent = text.replace(/([a-z])([A-Z])/g, "$1 $2");
          }
        });

        // Ensure borders are visible
        const borderedElements =
          clonedElement.querySelectorAll('[class*="border"]');
        borderedElements.forEach((el) => {
          const element = el as HTMLElement;
          const computed = window.getComputedStyle(el);

          // Force border styles
          if (computed.borderWidth && computed.borderWidth !== "0px") {
            element.style.borderStyle = "solid";
            element.style.borderColor =
              computed.borderColor || (isDarkMode ? "#374151" : "#e5e7eb");
            element.style.borderWidth = computed.borderWidth;
          }

          // Preserve border radius
          if (computed.borderRadius && computed.borderRadius !== "0px") {
            element.style.borderRadius = computed.borderRadius;
          }

          // Preserve box shadow
          if (computed.boxShadow && computed.boxShadow !== "none") {
            element.style.boxShadow = computed.boxShadow;
          }
        });

        // Fix button rendering
        const buttons = clonedElement.querySelectorAll(
          'button, [role="button"]',
        );
        buttons.forEach((btn) => {
          const button = btn as HTMLElement;
          const computed = window.getComputedStyle(btn);

          // Apply computed styles directly
          button.style.padding = computed.padding;
          button.style.backgroundColor = computed.backgroundColor;
          button.style.color = computed.color;
          button.style.border = computed.border;
          button.style.borderRadius = computed.borderRadius;
          button.style.fontSize = computed.fontSize;
          button.style.fontWeight = computed.fontWeight;
          button.style.lineHeight = computed.lineHeight;
          button.style.textAlign = computed.textAlign;
          button.style.cursor = "pointer";
          button.style.display = computed.display;

          // Remove any webkit appearance
          button.style.webkitAppearance = "none";
          button.style.appearance = "none";
        });

        // Ensure container has proper dimensions
        clonedElement.style.width = `${contentWidth}px`;
        clonedElement.style.minWidth = `${contentWidth}px`;
        clonedElement.style.position = "relative";
      },
    });

    if (!blob) {
      throw new Error("Failed to capture dashboard");
    }

    // Create image from blob to get dimensions and create 16:9 canvas
    const img = new Image();
    const imgUrl = URL.createObjectURL(blob);

    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
      img.src = imgUrl;
    });

    // Calculate 16:9 dimensions
    const aspectRatio = 16 / 9;
    let finalWidth = img.width;
    let finalHeight = Math.round(finalWidth / aspectRatio);

    // Ensure we don't crop content
    if (finalHeight < img.height) {
      finalHeight = img.height;
      finalWidth = Math.round(finalHeight * aspectRatio);
    }

    // Ensure minimum HD resolution
    if (finalWidth < 1920) {
      finalWidth = 1920;
      finalHeight = 1080;
    }

    // Create final 16:9 canvas
    const canvas = document.createElement("canvas");
    canvas.width = finalWidth;
    canvas.height = finalHeight;

    const ctx = canvas.getContext("2d", {
      alpha: false,
      desynchronized: true,
    });

    if (!ctx) {
      throw new Error("Could not get canvas context");
    }

    // High quality rendering settings
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";

    // Fill background
    ctx.fillStyle = backgroundColor;
    ctx.fillRect(0, 0, finalWidth, finalHeight);

    // Calculate scaling and positioning
    const scale = Math.min(finalWidth / img.width, finalHeight / img.height);
    const scaledWidth = img.width * scale;
    const scaledHeight = img.height * scale;
    const x = (finalWidth - scaledWidth) / 2;
    const y = (finalHeight - scaledHeight) / 2;

    // Draw the screenshot centered and scaled
    ctx.drawImage(img, x, y, scaledWidth, scaledHeight);

    // Clean up
    URL.revokeObjectURL(imgUrl);

    const finalBlob = await blobFromCanvas(canvas);
    downloadBlob(finalBlob, generateFilename());
    log.info(
      "Screenshot exported successfully",
      { width: finalWidth, height: finalHeight },
      "export",
    );
  } catch (error) {
    log.error("Error exporting screenshot", error, "export");

    // Try simpler method
    log.info("Trying simplified method", {}, "export");
    await exportDashboardSimplified();
  } finally {
    restoreScrollContainers();
  }
};

/**
 * Simplified export method with minimal options
 */
export const exportDashboardSimplified = async (): Promise<void> => {
  let restoreScrollContainers: () => void = () => {};
  try {
    const dashboardElement =
      (document.querySelector("[data-dashboard]") as HTMLElement) ||
      document.body;

    restoreScrollContainers = adjustScrollContainersForExport();

    // Simple capture
    const dataUrl = await toPng(dashboardElement, {
      quality: 1.0,
      pixelRatio: 2,
      backgroundColor: "#ffffff",
    });

    // Download directly
    const link = document.createElement("a");
    link.href = dataUrl;
    link.download = generateFilename();
    link.click();

    log.info("Screenshot exported using simplified method", {}, "export");
  } catch (error) {
    log.error("Simplified export also failed", error, "export");
    throw new Error(
      "Failed to export screenshot. Please try using your browser's print function (Ctrl+P).",
    );
  } finally {
    restoreScrollContainers();
  }
};

/**
 * Utility function to wait for page resources to load
 */
async function waitForPageLoad(): Promise<void> {
  // Wait for fonts
  if (document.fonts) {
    await document.fonts.ready;
  }

  // Wait for images
  const images = Array.from(document.images);
  await Promise.all(
    images
      .filter((img) => !img.complete)
      .map(
        (img) =>
          new Promise((resolve) => {
            img.addEventListener("load", resolve, { once: true });
            img.addEventListener("error", resolve, { once: true });
          }),
      ),
  );

  // Wait for next frame
  await new Promise((resolve) => requestAnimationFrame(resolve));

  // Additional wait for any animations
  await new Promise((resolve) => setTimeout(resolve, 300));
}

/**
 * Utility function to download a blob
 */
function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  setTimeout(() => URL.revokeObjectURL(url), 100);
}

/**
 * Generate filename with timestamp
 */
function generateFilename(): string {
  const timestamp = new Date().toISOString().slice(0, 19).replace(/[:-]/g, "");
  return `walmart-dashboard-${timestamp}.png`;
}

/**
 * Debug positioning issues
 */
export const debugPositioning = (): void => {
  const dashboard = document.querySelector("[data-dashboard]") as HTMLElement;
  if (!dashboard) {
    log.error("No dashboard found", {}, "export-debug");
    return;
  }

  const rect = dashboard.getBoundingClientRect();
  const computed = window.getComputedStyle(dashboard);

  log.debug(
    "Dashboard positioning debug",
    {
      boundingClientRect: {
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      },
      computedStyles: {
        margin: computed.margin,
        padding: computed.padding,
        position: computed.position,
        top: computed.top,
        left: computed.left,
        transform: computed.transform,
      },
      scrollDimensions: {
        scrollWidth: dashboard.scrollWidth,
        scrollHeight: dashboard.scrollHeight,
        offsetWidth: dashboard.offsetWidth,
        offsetHeight: dashboard.offsetHeight,
        clientWidth: dashboard.clientWidth,
        clientHeight: dashboard.clientHeight,
      },
      parentElement: dashboard.parentElement
        ? {
            tagName: dashboard.parentElement.tagName,
            rect: dashboard.parentElement.getBoundingClientRect(),
          }
        : null,
    },
    "export-debug",
  );
};

/**
 * Alternative CSS fix that can be applied before export
 */
export const applyExportFixes = (): void => {
  const style = document.createElement("style");
  style.id = "export-fixes";
  const exportFixStyles = `
    /* Force border visibility */
    [class*="border"] {
      border-style: solid !important;
    }

    /* Fix button rendering */
    button, [role="button"] {
      -webkit-appearance: none !important;
      appearance: none !important;
    }

    /* Ensure text spacing */
    * {
      letter-spacing: normal !important;
    }

    /* Fix chart containers */
    .chart-container, .card {
      position: relative !important;
    }
  `;
  style.appendChild(document.createTextNode(exportFixStyles));

  document.head.appendChild(style);

  // Remove after 5 seconds
  setTimeout(() => {
    const exportStyle = document.getElementById("export-fixes");
    if (exportStyle) {
      exportStyle.remove();
    }
  }, 5000);
};

type ExportCanvasOptions = {
  fileName?: string;
  backgroundColor?: string;
  pixelRatio?: number;
};

export const exportWidescreenCanvas = async (
  element: HTMLElement,
  options: ExportCanvasOptions = {},
): Promise<void> => {
  const { fileName = "widescreen-dashboard.png", pixelRatio = 2 } = options;
  const dataUrl = await toPng(element, {
    pixelRatio,
    cacheBust: true,
    backgroundColor: options.backgroundColor ?? "#0f172a",
    style: {
      transform: "scale(1)",
      transformOrigin: "top left",
    },
  });

  const link = document.createElement("a");
  link.download = fileName.endsWith(".png") ? fileName : `${fileName}.png`;
  link.href = dataUrl;
  link.click();
};
